import { io } from 'socket.io-client';
import { SOCKET_URL } from '../config';

class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
  }

  // Initialize socket connection
  connect(userId = null) {
    if (this.socket?.connected) {
      console.log('🔌 Socket already connected');
      return;
    }

    this.socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling']
    });

    this.socket.on('connect', () => {
      console.log('🚀 Connected to Socket.IO server:', this.socket.id);
      
      // Join user-specific room if userId provided
      if (userId) {
        this.socket.emit('join_user_room', userId);
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('📡 Disconnected from Socket.IO server:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('🚨 Socket connection error:', error);
    });

    // Set up real-time event listeners
    this.setupRealTimeListeners();
  }

  // Disconnect socket
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      console.log('🔌 Socket disconnected');
    }
  }

  // Set up listeners for real-time listing events
  setupRealTimeListeners() {
    if (!this.socket) return;

    // Listen for new listings
    this.socket.on('listing_created', (listing) => {
      console.log('📦 New listing received:', listing.title);
      this.notifyListeners('listing_created', listing);
    });

    // Listen for updated listings
    this.socket.on('listing_updated', (listing) => {
      console.log('🔄 Listing updated:', listing.title);
      this.notifyListeners('listing_updated', listing);
    });

    // Listen for deleted listings
    this.socket.on('listing_deleted', (data) => {
      console.log('🗑️ Listing deleted:', data.title);
      this.notifyListeners('listing_deleted', data);
    });

    // Listen for claimed listings
    this.socket.on('listing_claimed', (listing) => {
      console.log('🎯 Listing claimed:', listing.title);
      this.notifyListeners('listing_claimed', listing);
    });

    // Purchase events
    this.socket.on('purchase_paid', (payload) => {
      this.notifyListeners('purchase_paid', payload);
    });
    this.socket.on('sale_paid', (payload) => {
      this.notifyListeners('sale_paid', payload);
    });
    this.socket.on('purchase_captured', (payload) => {
      this.notifyListeners('purchase_captured', payload);
    });
    this.socket.on('sale_captured', (payload) => {
      this.notifyListeners('sale_captured', payload);
    });
    this.socket.on('transaction_completed', (payload) => {
      this.notifyListeners('transaction_completed', payload);
    });

    // Buying request events
    this.socket.on('buying_request_received', (payload) => {
      this.notifyListeners('buying_request_received', payload);
    });
    this.socket.on('buying_request_approved', (payload) => {
      this.notifyListeners('buying_request_approved', payload);
    });
    this.socket.on('buying_request_declined', (payload) => {
      this.notifyListeners('buying_request_declined', payload);
    });
  }

  // Add event listener
  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  // Remove event listener
  removeEventListener(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  // Notify all listeners for an event
  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in socket listener for ${event}:`, error);
        }
      });
    }
  }

  // Get connection status
  isConnected() {
    return this.socket?.connected || false;
  }

  // --- CHAT HELPERS ---
  joinRoom(roomId) {
    if (this.socket) this.socket.emit('join_room', roomId);
  }
  leaveRoom(roomId) {
    if (this.socket) this.socket.emit('leave_room', roomId);
  }
  sendMessage(roomId, text) {
    if (this.socket) this.socket.emit('message_send', { roomId, text });
  }
  onMessage(callback) {
    if (this.socket) this.socket.on('message_recv', callback);
  }
  offMessage(callback) {
    if (this.socket) this.socket.off('message_recv', callback);
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService; 